<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionsConfig"
      :has-return="true"
    />
    <div class="row">
      <div class="col mb-4 color-dark-gray font-weight-bold font-16 d-flex align-items-center">
        <queue-report-status 
          :queue-report="general.attachmentIpReport"
          @on-download="onDownloadHandler"
          @on-cancel="onCancelHandler"
        />
      </div>
    </div>
    <progress-filter
      :academic-years="academicYears"
      :default-value="lastAcademicYear"
      @on-change-academic-year="onChangeAcademicYearHandler"
    />
    <section-title
      class="mb-3 pl-1 ml-1 section-title__extended"
      icon="fas fa-clipboard-list-check"
      title="Resumen de Información General"
      title-class="font-16"
    />
    <div class="row">
      <div class="col col-sm col-12 ml-2">
        <general-info-summary
          title="Estudiantes"
          total-title="Total de Estudiantes"
          pending-title="Estudiantes Pendientes"
          completed-title="Por ciento de Estudiantes Completados"
          :total-count="studentsSummary.totalCount"
          :pending-count="studentsSummary.pendingCount"
          :completed-count="studentsSummary.completedPercentage"
        />
      </div>
      <div class="col col-sm col-12 ml-2 mt-2 mt-sm-2 mt-md-0">
        <general-info-summary
          title="Servicios"
          :total-count="servicesSummary.totalCount"
          :pending-count="servicesSummary.pendingCount"
          :completed-count="servicesSummary.completedPercentage"
        />
      </div>
    </div>
    <div class="ml-2 mt-3">
      <summary-collapse
        title="Detalle de Resumen Actual"
        :is-opened="true"
        :action-summary="actionSummary"
        :filter-items="peiTypes"
        :current-ip-report="general.currentIpReport"
        @on-change-filter-item="onChangeFilterItemHandler"
        @on-download="onDownloadHandler"
        @on-cancel="onCancelHandler"
      >
        <progress-info-summary :items="currentProgressSummary" />
      </summary-collapse>
    </div>
    <div class="mt-4 ml-2">
      <section-title has-line title="Historial de Informes de Progreso" />
      <progress-info-history
        :items="progressHistory"
        :action-items="actionItems"
        @on-refresh="onRefreshHandler"
        @on-close-ip="onCloseIpHandler"
        @on-update-ip="onUpdateIpHandler"
        @on-open-detail="onOpenDetailHandler"
        @on-download="onDownloadHandler"
        @on-cancel="onCancelHandler"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from "PageHeader";
import SectionTitle from "SectionTitle";
import {
  ProgressFilter,
  GeneralInfoSummary,
  SummaryCollapse,
  ProgressInfoSummary,
  ProgressInfoHistory,
  QueueReportStatus,
} from "../shared/components";
import Service from "@/services/api/progress.api";
import DocumentServices from "@/modules/report/document";
import stipulationType from "@/utils/constants/stipulationType.js";
import listAllPeiTypes from "@/utils/constants/listAllPeiTypes";
import queueStatus from "@/utils/constants/queueReportStatus.js";
import queueReportTypes from "@/utils/constants/queueReportTypes.js";
import hubservice from "@/services/api/hub/hubservice.js";
import Vue from "vue";

export default {
  components: {
    PageHeader,
    SectionTitle,
    ProgressFilter,
    GeneralInfoSummary,
    SummaryCollapse,
    ProgressInfoSummary,
    ProgressInfoHistory,
    QueueReportStatus,
  },
  data: () => ({
    academicYears: [],
    selectedAcademicYear: null,
    selectedPeiType: listAllPeiTypes[0],
    general: {
      attachmentIpReport: {
        executionLogId: null,
        statusId: null,
        statusMessage: null,
        endExecutionDate: null,
        expired: false,
      },
      currentIpReport: {
        executionLogId: null,
        statusId: null,
        statusMessage: null,
        endExecutionDate: null,
        expired: false,
      }
    },
    studentsSummary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    servicesSummary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    progressHistory: [],
    currentProgressSummary: [],
    showUpdate: true,
  }),
  computed: {
    stipulationType() {
      return stipulationType.ST40;
    },
    peiTypes() {
      return listAllPeiTypes;
    },
    pageConfig() {
      return {
        title: "Informes de Progreso: Estipulación 40",
        icon: "fas fa-calculator",
        routes: ["Inicio", "Informes de Progreso", "Estipulación 40"],
      };
    },
    pageActionsConfig() {
      return [
        {
          text: "Generar Anejo",
          icon: "fas fa-file-plus pr-2",
          variant: "outline-success",
          visible: this.showGenerate,
          click: () => this.onGenerateAttachment(),
        },
        {
          text: "Actualizar IP",
          icon: "fas fa-sync-alt pr-2",
          variant: "success",
          visible: this.showUpdate,
          click: () => this.onUpdateProgressReportHandler(),
        },
      ];
    },
    actionSummary() {
      return () => {
        return [
          {
            label: "",
            icon: "fas fa-file-plus text-green",
            class: "pr-1",
            visible: true,
            click: () => this.onGenerateSummaryHandler(),
          },
        ];
      };
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: "",
            icon: "fas fa-file-plus text-green",
            class: "pr-1",
            visible: true,
            click: () => this.onGenerateHandler(item),
          },
        ];
      };
    },
    isLastIpClosed() {
      if (!this.progressHistory.length) return true;
      return this.progressHistory[0].isClosed;
    },
    lastAcademicYear() {
      if (!this.academicYears?.length) {
        return null;
      }

      return this.academicYears.reduce((max, academicYear) =>
        max.id > academicYear.id ? max : academicYear
      );
    },
    isLastAcademicYearSelected() {
      if (!this.academicYears?.length) {
        return false;
      }

      return this.lastAcademicYear?.id == this.selectedAcademicYear?.id;
    },
    showGenerate() {
      return ![queueStatus.Pending, queueStatus.Running]
        .includes(this.general?.attachmentIpReport?.statusId);
    },
  },
  async mounted() {
    await this.getInit();
  },
  created() {
    Vue.use(hubservice)
    this.$userHub.$on('hub-receive-status-queue-report', this.updateReceivedQueueStatusByHub)
  },
  beforeDestroy() {
    //clean SignalR event
    this.$userHub.$off('hub-receive-status-queue-report', this.updateReceivedQueueStatusByHub)
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
      if (this.academicYears?.length) {
        this.selectedAcademicYear = this.lastAcademicYear;
        await this.getDetail();
      }
    },
    async getAvailableAcademicYears() {
      this.academicYears = (
        await Service.getAcademicYears(this.stipulationType)
      ).data;
    },
    async getDetail() {
      const response = (
        await Service.getDetail({
          stipulationType: this.stipulationType,
          academicYearId: this.selectedAcademicYear.id,
          filter: this.selectedPeiType.name
        })
      ).data;
      if (response) {
        this.general = response.general;
        this.studentsSummary = response.summaries[0];
        this.servicesSummary = response.summaries[1];
        this.currentProgressSummary = response.currentSummaryDetail;
        this.progressHistory = response.progressHistory;
      }
    },
    async onRefreshHandler() {
      await this.getDetail();
    },
    async onRefreshTimerHandler(milliseconds = 90000) {
      this.showUpdate = false;
      setTimeout(async function() {
        await this.onRefreshHandler();
        this.showUpdate = true;
      }.bind(this), milliseconds);
    },
    onChangeAcademicYearHandler(academicYear) {
      this.selectedAcademicYear = academicYear;
      this.getDetail();
    },
    onChangeFilterItemHandler(peiType) {
      this.selectedPeiType = peiType;
      this.getDetail();
    },
    async onUpdateIpHandler() {
      await Service.updateIp(this.stipulationType, this.selectedAcademicYear.id, true).then(async (res) => {
        if (res.data) {
          await this.onRefreshHandler();
          this.ShowToast(
            "Proceso Exitoso",
            "El IP ha sido actualizado exitosamente.",
            "success"
          );
        } else {
          this.ShowToast(
            "Alerta",
            "No cuenta con datos para actualizar en sistema.",
            "error"
          );
        }
      });
    },
    async onUpdateProgressReportHandler() {
      if (!this.isLastIpClosed) {
        this.ShowToast(
          "Alerta",
          "Para crear una nueva actualización es necesario cerrar el último IP.",
          "error"
        );
      } else {
        const answer = await this.ShowCancelModal(
          "Alerta",
          "¿Está seguro que desea actualizar los datos y generar un IP?",
          "Sí",
          "No"
        );
        if (!answer) return;
        
        let allClosed = await this.allClosed();
        if (!allClosed) {
          this.ShowToast(
            "Alerta",
            "No es posible generar un IP, ya que es necesario cerrar el último IP en todos los años académicos.",
            "error"
          );

          return;
        }
        
        Service.updateIp(this.stipulationType, this.selectedAcademicYear.id, false).then((res) => {
          if (res.data) {
            this.onRefreshTimerHandler();
            this.ShowToast(
              "Proceso Exitoso",
              "Se ha iniciado el proceso de actualización de IP exitosamente.",
              "warning"
            );
          } else {
            this.ShowToast(
              "Alerta",
              "No es posible generar un IP, ya que no cuenta con datos para actualizar en sistema.",
              "error"
            );
          }
        });
      }
    },
    async allClosed() {
      for (let academicYear of this.academicYears) {
        let closed = (await Service.isLastIpClosed(this.stipulationType, academicYear.id)).data;
        if (!closed) {
          return false;
        }
      }
      return true;
    },
    async onCloseIpHandler({ summaryId }) {
      if (summaryId) {
        this.ShowCancelModal(
          "Alerta",
          "¿Está seguro que desea congelar los datos y cerrar un IP?",
          "Sí",
          "No"
        ).then(async (res) => {
          if (res) {
            const result = (
              await Service.closeIp(this.stipulationType, summaryId)
            ).data;
            if (result) {
              this.onRefreshHandler();
              this.ShowToast(
                "Proceso Exitoso",
                "El IP ha sido cerrado exitosamente.",
                "success"
              );
            }
          }
        });
      }
    },
    async onGenerateSummaryHandler() {
      if (this.selectedAcademicYear) {
        var academicYearId = this.selectedAcademicYear.id;
        this.onGenerateHandler({ academicYearId, ipSequence: 0 });
      }
    },
    async onGenerateHandler(item) {
      const payload = {
        stipulationType: this.stipulationType,
        summaryId: item?.summaryId,
        ipSequence: item.ipSequence,
        academicYearId: item.academicYearId,
      };
      
      await Service.queueIpReport(payload)
        .then((res) => {
          if (res.data) {
            this.onRefreshHandler();
            this.ShowToast(
              "Proceso Exitoso",
              `El reporte IP  ${item.ipSequence} ha sido programado exitosamente.`,
              "success"
            );
          }
        })
        .catch((err) => {
          this.ShowToast("", err.response?.data?.message, "error");
        });
    },
    async onCancelHandler(executionLogId) {
      await Service.cancelQueuedReport(this.stipulationType, executionLogId)
        .then((res) => {
          if (res.data) {
            this.ShowToast(
              'Proceso Exitoso',
              'La ejecución del reporte ha sido cancelada exitosamente.',
              'success'
            );
            this.onRefreshHandler();
          }
        })
        .catch((err) => {
          this.ShowToast("", err.response?.data?.message, "error");
        });
    },
    async onDownloadHandler(executionLogId) {
      await Service.downloadQueuedReport(this.stipulationType, executionLogId)
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast("", err.response?.data?.message, "error");
        });
    },
    async onGenerateAttachment() {
      await Service.queueIpAttachment(this.stipulationType)
      .then(async (res) => {
        if (res.data) {
          await this.onRefreshHandler();
          this.ShowToast(
            "Proceso Exitoso",
            "El anejo ha sido programado exitosamente.",
            "success"
          );
        }
      })
      .catch((err) => {
        this.ShowToast("", err.response?.data?.message, "error");
      });
    },
    async onOpenDetailHandler(index) {
      this.progressHistory = await Promise.all(this.progressHistory.map(async (item, itemIndex) => {
        if(itemIndex === index) {
          item.isOpen = index === itemIndex ? !item.isOpen : false;
          if (item.isOpen && !item.summary) {
            item.summary = (await Service.getIpHistory(this.stipulationType, this.selectedAcademicYear.id, item.ipSequence)).data;
          }
        }
        return item;
      }));
    },
    updateReceivedQueueStatusByHub(res) {
      const infoUpdated = res?.respose;

      if(infoUpdated.reportTypeId === queueReportTypes.DetailIpReport) {
        const index = this.progressHistory.findIndex((item) => {
          return item.detailIpReport.executionLogId === infoUpdated.executionLogId
        });

        this.progressHistory[index].detailIpReport.statusId = infoUpdated.statusId;
        this.progressHistory[index].detailIpReport.statusMessage = infoUpdated.statusMessage;
        return;
      }
      if (infoUpdated.reportTypeId === queueReportTypes.CurrentIpReport) {
        this.general.currentIpReport.statusId = infoUpdated.statusId;
        this.general.currentIpReport.statusMessage = infoUpdated.statusMessage;
      }
      if (infoUpdated.reportTypeId === queueReportTypes.AttachmentIpReport) {
        this.general.attachmentIpReport.statusId = infoUpdated.statusId;
        this.general.attachmentIpReport.statusMessage = infoUpdated.statusMessage;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/views/progress/shared/scss/common.scss";
</style>